import { PAYMENT_PROVIDERS } from "@/pages/orders/_graphql/query";
import { CONFIRM_OFFLINE_TRANSFER } from "@/pages/payment/graphql/mutation";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  PinInput,
  PinInputField,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import {
  FaArrowRight,
  FaCheckCircle,
  FaStopCircle,
  FaSync,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import {
  APPROVE_THIRD_PARTY_PAYOUT,
  FINALIZE_THIRD_PARTY_PAYOUT,
  getTransferBadgeColor,
  GET_THIRD_PARTY_PAYOUT,
} from "../../../../features/third-party-payouts/constants";
import { ThirdPartyIntegratorPayout } from "../../../../features/third-party-payouts/types";
import { forceActualDay, formatAsNaira } from "../../../../helpers/helpers";
import { PaymentProvider, TransferStatus } from "../../../../types";
import Loading from "../../../../_components/Loading";
import RequestFailed from "../../../../_components/RequestFailed";

export default function Page() {
  const { id } = useParams<{
    id: string;
  }>();

  const { data, loading, error, refetch } = useQuery<{
    thirdPartyIntegratorPayout: ThirdPartyIntegratorPayout | null;
  }>(GET_THIRD_PARTY_PAYOUT, {
    variables: {
      id,
    },
  });

  const {
    loading: paymentProvidersLoading,
    data: paymentProvidersData,
    error: paymentProvidersError,
  } = useQuery<{
    paymentProviders: PaymentProvider[];
  }>(PAYMENT_PROVIDERS);

  const paymentProviders = paymentProvidersData?.paymentProviders;

  const [approvePayout, approvePayoutResponse] = useMutation(
    APPROVE_THIRD_PARTY_PAYOUT,
    {
      onError: () => {},
    }
  );

  const [finalizePayout, finalizePayoutResponse] = useMutation(
    FINALIZE_THIRD_PARTY_PAYOUT,
    {
      onError: () => {},
    }
  );

  const [confirmOfflineTransfer, confirmOfflineTransferCall] = useMutation(
    CONFIRM_OFFLINE_TRANSFER,
    {
      onError: () => {},
      onCompleted: () => {
        refetch();
      },
    }
  );

  const [otp, setOtp] = useState<string>();
  const [radioValue, setRadioValue] = useState<string>();
  const [
    offlineTransactionReference,
    setOfflineTransactionReference,
  ] = useState<string>();
  const [sourceLaborHackAccount, setSourceLaborHackAccount] = useState<string>(
    "Access Bank"
  );
  const [offlineTransactionDate, setOfflineTransactionDate] = useState<Date>();

  const submitOfflineTransferDetails = async () => {
    if (!offlineTransactionReference || !offlineTransactionDate) return;

    confirmOfflineTransfer({
      variables: {
        transferId: data?.thirdPartyIntegratorPayout?.transfer?.id,
        transactionReference: offlineTransactionReference,
        transactionDate: new Date(offlineTransactionDate),
        bankName: sourceLaborHackAccount,
      },
    });
  };

  const isApprovable =
    data?.thirdPartyIntegratorPayout?.transfer?.status ===
    TransferStatus.AWAITING_APPROVAL;

  if (loading) return <Loading />;

  if (error) return <RequestFailed />;

  if (!data?.thirdPartyIntegratorPayout)
    return <RequestFailed errorMessage='Not Found' />;

  const {
    breakdown,
    override,
    thirdPartyIntegrator,
    orders,
    transfer,
    payoutPeriodStart,
    payoutPeriodEnd,
  } = data.thirdPartyIntegratorPayout;

  // Find Paystack provider from the payment provider information
  const paystackProvider = thirdPartyIntegrator.paymentProviderInformation?.providerData
    ?.flatMap((data) => data.providers)
    .find((provider) => provider.providerId === "paystack");

  const paymentInformation = paystackProvider?.bankInformation;

  return (
    <div className='grid grid-cols-3 gap-4'>
      <div className='col-span-2'>
        <div className='flex flex-col gap-8 p-6 bg-white border'>
          <div className='mb-4'>
            <h2 className='text-primary-500 text-lg font-medium m-0'>
              Third Party Integrator Payout Confirmation #{id.slice(0, 8)}
            </h2>
            <p className='m-0 text-primary-300 text-sm'>
              Shows a detailed view of a third party integrator payout
              confirmation
            </p>
          </div>

          <div className='grid grid-cols-[1fr,2fr] gap-2 max-w-md'>
            <p className='m-0 text-primary-300 text-sm'>Integrator Name</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.name}
            </p>

            <p className='m-0 text-primary-300 text-sm'>App ID</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.appId}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Client Business</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.client.business?.name || "N/A"}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Client Contact</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.client.firstName}{" "}
              {thirdPartyIntegrator.client.lastName}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Client Email</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.client.email}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Payout Period</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {new Date(payoutPeriodStart).toDateString()} -{" "}
              {new Date(payoutPeriodEnd).toDateString()}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Service Fee Share</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.policy.serviceFeeSharePercentage}%
            </p>
          </div>

          <Divider />

          <div>
            <h3 className='text-primary-500 text-md font-medium mb-3'>
              Orders Included ({orders.length})
            </h3>
            <TableContainer>
              <Table size='sm' className='border'>
                <Thead>
                  <Tr>
                    <Th className='border'>Order Code</Th>
                    <Th className='border'>Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orders.map((order) => (
                    <Tr key={order.id}>
                      <Td className='border'>{order.orderCode}</Td>
                      <Td className='border'>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>

      <div>
        <div className='flex flex-col gap-6 p-6 bg-white border'>
          <div>
            <h2 className='text-primary-500 text-lg font-medium m-0'>
              Breakdown
            </h2>
          </div>
          <Divider />

          <div className='flex flex-col gap-4'>
            <h3 className='font-medium text-primary-500 text-sm'>
              Payment Information
            </h3>
            {!paymentInformation && (
              <Alert status='error'>
                <AlertIcon />
                <AlertTitle className='text-sm !font-medium'>
                  No payment information found
                </AlertTitle>
                <AlertDescription></AlertDescription>
              </Alert>
            )}
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Name</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {paymentInformation?.accountName || "N/A"}
                </p>
              </div>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>AC Number</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {paymentInformation?.accountNumber || "N/A"}
                </p>
              </div>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Bank Name</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {paymentInformation?.bankName || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <Divider />

          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Status</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  <Badge
                    colorScheme={getTransferBadgeColor(
                      transfer?.status || TransferStatus.PENDING
                    )}
                  >
                    {transfer?.status?.replace("_", " ") ||
                      TransferStatus.PENDING}
                  </Badge>
                </p>
              </div>

              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>
                  Gross Amount (NGN)
                </p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {formatAsNaira(breakdown.gross)}
                </p>
              </div>

              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Overridden</p>
                {override ? (
                  <FaCheckCircle className='text-green-500' />
                ) : (
                  <FaStopCircle className='text-red-500' />
                )}
              </div>
            </div>
          </div>

          <Divider />

          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Net Amount (NGN)</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {formatAsNaira(breakdown.net)}
                </p>
              </div>
            </div>
          </div>

          {isApprovable && (
            <div>
              <Divider />
              <div className='mt-4'>
                <RadioGroup>
                  {paymentProviders?.map((provider) => (
                    <Stack direction='column' key={provider.id}>
                      <Radio
                        value={provider.id}
                        onChange={(e) => setRadioValue(e.target.value)}
                      >
                        Pay with {provider.name}
                      </Radio>
                    </Stack>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          <Divider />

          {isApprovable && radioValue === "offline" && (
            <div className='space-y-6'>
              <VStack spacing={8} alignItems={"start"} w={"80%"}>
                <Box width={"full"}>
                  <FormLabel>Offline Transaction Reference</FormLabel>
                  <Input
                    value={offlineTransactionReference}
                    onChange={(e) =>
                      setOfflineTransactionReference(e.target.value)
                    }
                  />
                </Box>
                <Box width={"full"}>
                  <FormLabel>Source LaborHack Account</FormLabel>
                  <Select
                    value={sourceLaborHackAccount}
                    onChange={(e) => setSourceLaborHackAccount(e.target.value)}
                  >
                    <option value='Access Bank'>Access Bank</option>
                  </Select>
                </Box>
                <Box width={"full"}>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type='date'
                    value={offlineTransactionDate?.toISOString().split("T")[0]}
                    max={forceActualDay(new Date()).toISOString().split("T")[0]}
                    onChange={(e) =>
                      setOfflineTransactionDate(new Date(e.target.value))
                    }
                  />
                </Box>
              </VStack>
              <Divider />
              <Box width={"full"}>
                <Button
                  colorScheme='green'
                  rightIcon={<FaArrowRight className='inline-block' />}
                  width={"full"}
                  textAlign={"center"}
                  size={"md"}
                  isLoading={confirmOfflineTransferCall.loading}
                  isDisabled={
                    !offlineTransactionReference || !offlineTransactionDate
                  }
                  onClick={submitOfflineTransferDetails}
                >
                  Approve Payout
                </Button>
              </Box>
            </div>
          )}

          {isApprovable && radioValue === "paystack" && (
            <div className='flex flex-col gap-2'>
              {!transfer?.transferCode && (
                <Button
                  colorScheme='green'
                  rightIcon={<FaArrowRight className='inline-block' />}
                  isLoading={approvePayoutResponse.loading}
                  isDisabled={!paymentInformation}
                  onClick={() => {
                    approvePayout({
                      variables: {
                        payoutId: id,
                        providerId: "paystack",
                      },
                    });
                  }}
                >
                  Approve Payout
                </Button>
              )}
              {transfer?.transferCode && (
                <>
                  <FormControl isInvalid={!!finalizePayoutResponse.error}>
                    <FormLabel>Enter OTP</FormLabel>
                    <HStack>
                      <HStack>
                        <PinInput
                          otp
                          onComplete={(otp) => {
                            setOtp(otp);
                          }}
                          onChange={() => {
                            finalizePayoutResponse.reset();
                          }}
                        >
                          <PinInputField />
                          <PinInputField />
                          <PinInputField />
                          <PinInputField />
                          <PinInputField />
                          <PinInputField />
                        </PinInput>
                      </HStack>

                      <Button
                        colorScheme='green'
                        rightIcon={<FaCheckCircle className='inline-block' />}
                        isLoading={finalizePayoutResponse.loading}
                        isDisabled={!otp}
                        onClick={() => {
                          finalizePayout({
                            variables: {
                              payoutId: id,
                              otp,
                            },
                          });
                        }}
                      >
                        Finalize Payout
                      </Button>
                    </HStack>
                    <FormErrorMessage>
                      {finalizePayoutResponse.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                  <div className='flex justify-start'>
                    <Button
                      variant='link'
                      leftIcon={<FaSync className='inline-block' />}
                      isLoading={approvePayoutResponse.loading}
                      onClick={() => {
                        approvePayout({
                          variables: {
                            payoutId: id,
                            providerId: "paystack",
                          },
                        });
                      }}
                    >
                      Refresh OTP
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
